import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import ProfileTab from './ProfileTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import { clearTenantUniqueCode, getTenantLoginUrl, saveTenantUniqueCode } from 'utils/tenantRouting';

import { isPrivateProvider } from 'types/user';

// assets
import { useGetCurrentUserModal, useGetProfilePicture } from 'api/currentUser';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const handleLogout = async () => {
    try {
      if (user?.userType === 1) {
        clearTenantUniqueCode();
        localStorage.setItem('logout-redirect', '/login');
      } else {
        const uniqueCode = user?.uniqueCode;
        if (uniqueCode) {
          saveTenantUniqueCode(uniqueCode);
        }
        const targetLoginUrl = getTenantLoginUrl(uniqueCode);
        if (targetLoginUrl !== '/login') {
          localStorage.setItem('logout-redirect', targetLoginUrl);
        }
      }

      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const { data: CurrentUser } = useGetCurrentUserModal();
  const currentUser = Array.isArray(CurrentUser) ? CurrentUser[0] : CurrentUser;
  const displayName = currentUser?.fullName || '';

  const handleEditProfile = () => {
    setOpen(false);
    navigate('/apps/profiles/user');
  };

  const handleMySignatures = () => {
    setOpen(false);
    navigate('/apps/profiles/signatures');
  };
  const { data: profilePicture } = useGetProfilePicture();
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? 'grey.100' : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 },
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent', '&:hover': { bgcolor: 'secondary.light' } })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
          <Avatar alt="profile user" src={profilePicture ?? undefined} size="sm" />
          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
            {user?.fullName}
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: 150, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar alt="profile user" src={profilePicture ?? undefined} sx={{ width: 32, height: 32 }} />
                          <Stack sx={{ maxWidth: 150, overflow: 'hidden' }}>
                            <Tooltip title={displayName} placement="top">
                              <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {displayName}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Grid>

                    </Grid>
                  </CardContent>

                  <ProfileTab
                    handleLogout={handleLogout}
                    handleEditProfile={handleEditProfile}
                    handleMySignatures={handleMySignatures}
                    showSignatures={isPrivateProvider(currentUser?.ppfUserType)}
                  />
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
